import {
  ActionButtonProps,
  Box,
  ColorOptions,
  Form,
  Icon,
  Loader,
  LoaderNew,
  ModBox,
  Text,
  allThemes,
} from '@jfs/ui-new'
import { useEffect, useState } from 'react'
import { RootStore, useLazyApiData, useStores } from '../../../data/store'
import { MessageTypes } from '../../../utils'
import { styles } from '../styles'
import { TableWithEditAddForm } from './TableWithEditAddForm'
import { CinDetailsForm, MerchantSearch } from './formData'

const FormBox = ({ children }: any) => {
  return <Box {...styles.formContainer}>{children}</Box>
}

const FiuCompliance = () => {
  const {
    searchMidrCin,
    updateDirectorChanges,
    updateUboChanges,
    updateAuthSigChanges,
    addUbo,
    addAuthorisedSignatory,
    addDirector,
    deleteDirector,
    deleteUbo,
    deleteAuthSignatory,
    uboListData,
    directorsListData,
    authorizedListData,
    cinData,
    updateCin,
    createUpdateDeleteFiuData,
    getDirectorChanges,
    getUboChanges,
    getAuthChanges,
    uboEditedList,
    directorsEditedList,
    authorizedEditedList,
    isFiuDataChanged,
    setFiuDataChanged,
    undoDirector,
    undoUbo,
    undoAuth,
    clearSubmittedFiuData,
    exportRecords,
  } = useStores((root) => ({
    searchMidrCin: root?.fetchSearchEntity,
    updateDirectorChanges: root?.updateDirectorChanges,
    updateUboChanges: root?.updateUboChanges,
    updateAuthSigChanges: root?.updateAuthSigChanges,
    addUbo: root?.addUbo,
    addAuthorisedSignatory: root?.addAuthorisedSignatory,
    addDirector: root?.addDirector,
    deleteDirector: root?.deleteDirector,
    deleteUbo: root?.deleteUbo,
    deleteAuthSignatory: root?.deleteAuthSignatory,
    uboListData: root?.getUboList,
    directorsListData: root?.getDirectorsList,
    authorizedListData: root?.getAuthSignotoriesList,
    directorsListDataLength: root?.fiu?.directors?.length,
    authorizedListDataLength: root?.fiu?.authSignatories?.length,
    ubosLength: root?.fiu?.ubos?.length,
    cinData: root?.fiu?.cin,
    updateCin: root?.updateCin,
    createUpdateDeleteFiuData: root?.createUpdateDeleteFiuData,
    getDirectorChanges: root?.getDirectorChanges,
    getUboChanges: root?.getUboChanges,
    getAuthChanges: root?.getAuthChanges,
    uboEditedList: root?.getEditedUboList,
    directorsEditedList: root?.getEditedDirectorsList,
    authorizedEditedList: root?.getEditedAuthSignotoriesList,
    isFiuDataChanged: root.fiu.isFiuDataChanged,
    setFiuDataChanged: root.setFiuDataChanged,
    undoDirector: root.undoDirector,
    undoUbo: root.undoUbo,
    undoAuth: root.undoAuth,
    clearSubmittedFiuData: root.clearSubmittedFiuData,
    exportRecords: root.exportFiuRecords,
  }))
  const [showDetails, setShowCinDetails] = useState(false)
  const [identificationNumber, setIdentificationNumber] = useState('')

  const [editedUboList, setEditedUboList] = useState([])
  const [editedDirList, setEditedDirectorsList] = useState([])
  const [editedAuthList, setEditedAuthList] = useState([])

  const [searchFin, searchFinHandler] = useLazyApiData(searchMidrCin)
  const [cinSubmit, cinSubmitHandler] = useLazyApiData(createUpdateDeleteFiuData)
  const [directorSubmit, directorSubmitHandler] = useLazyApiData(createUpdateDeleteFiuData)
  const [ubosSubmit, ubosSubmitHandler] = useLazyApiData(createUpdateDeleteFiuData)
  const [authsSubmit, authsSubmitHandler] = useLazyApiData(createUpdateDeleteFiuData)
  const [lazyExportFiuFile, exportFiuFileHandler] = useLazyApiData(exportRecords)

  useEffect(() => {
    if (isFiuDataChanged) {
      setEditedUboList(uboEditedList())
      setEditedDirectorsList(directorsEditedList())
      setEditedAuthList(authorizedEditedList())
      setFiuDataChanged(false)
    }
  }, [authorizedEditedList, directorsEditedList, isFiuDataChanged, setFiuDataChanged, uboEditedList])

  useEffect(() => {
    if (searchFinHandler?.state === 'fulfilled') {
      setShowCinDetails(searchFinHandler?.data !== "Record doesn't exist")
    }
  }, [searchFinHandler?.data, searchFinHandler?.state])

  useEffect(() => {
    if (directorSubmitHandler?.state === 'fulfilled') {
      if (directorSubmitHandler?.data?.status === 'Success') {
        setEditedDirectorsList([])
        clearSubmittedFiuData('director')
      }
    }
  }, [clearSubmittedFiuData, directorSubmitHandler?.data?.status, directorSubmitHandler?.state])

  useEffect(() => {
    if (ubosSubmitHandler?.state === 'fulfilled') {
      if (ubosSubmitHandler?.data?.status === 'Success') {
        setEditedUboList([])
        clearSubmittedFiuData('ubo')
      }
    }
  }, [clearSubmittedFiuData, ubosSubmitHandler?.data?.status, ubosSubmitHandler?.state])

  useEffect(() => {
    if (authsSubmitHandler?.state === 'fulfilled') {
      if (authsSubmitHandler?.data?.status === 'Success') {
        setEditedAuthList([])
        clearSubmittedFiuData('auth')
      }
    }
  }, [authsSubmitHandler?.data?.status, authsSubmitHandler?.state, clearSubmittedFiuData])

  useEffect(() => {
    if (directorSubmitHandler?.state === 'fulfilled' || directorSubmitHandler?.state === 'rejected') {
      setEditedDirectorsList([])
    }
    if (ubosSubmitHandler?.state === 'fulfilled' || ubosSubmitHandler?.state === 'rejected') {
      setEditedUboList([])
    }
    if (authsSubmitHandler?.state === 'fulfilled' || authsSubmitHandler?.state === 'rejected') {
      setEditedAuthList([])
    }
  }, [authsSubmitHandler?.state, cinSubmitHandler?.state, directorSubmitHandler?.state, ubosSubmitHandler?.state])

  const handleSubmit = ({ values, isValid }: ActionButtonProps) => {
    const handlePress = () => {
      if (values && values?.midCin && values?.midCin?.length > 0 && isValid && !isNaN(+values?.midCin)) {
        setIdentificationNumber(values?.midCin)
        searchFin(values?.midCin, 'Entity ID', 'fiu')
      } else {
        RootStore.message.setMessage('Enter valid MID to search', MessageTypes.ERROR)
      }
    }

    return (
      <Box
        {...styles.buttonBgView}
        disabled={values?.midCin === undefined}
        backgroundColor={
          values && values?.midCin && values?.midCin?.length > 2
            ? ('PRIMARY' as ColorOptions)
            : (allThemes.light.colors.DISABLED_GREY as ColorOptions)
        }
        onPress={handlePress}>
        {searchFinHandler?.state === 'pending' ? (
          <Loader type="small" color="WHITE" />
        ) : (
          <Text
            color={
              values && values?.midCin && values?.midCin?.length > 2
                ? (allThemes.light.colors.WHITE as ColorOptions)
                : (allThemes.light.colors.GRAY_LIGHT as ColorOptions)
            }
            type="body2">
            {'EMS Search'}
          </Text>
        )}
      </Box>
    )
  }

  const handleCinUpdateAction = ({ values, isValid }: ActionButtonProps) => {
    let isCinFormChanged = false
    const cinNumber = cinData?.number
    const cinDate = cinData?.date?.includes('T') ? cinData?.date?.split('T')?.[0] : cinData?.date ?? ''
    isCinFormChanged = cinNumber !== values?.cinNumber || cinDate !== values?.cinDate
    const handlePress = () => {
      if (values?.cinNumber?.length > 0 && values?.cinDate?.length > 0) {
        updateCin(values)
        if (identificationNumber?.length > 0) {
          const data = {
            cin: values?.cinNumber,
            dateOfIncorporation: values?.cinDate,
            action: 'UPDATE',
          }
          cinSubmit(identificationNumber, 'CIN', data)
        }
      }
    }

    return (
      <ModBox w={'100%'} justifyContent={'center'} alignItems={'center'}>
        <Box
          {...styles.buttonBgView}
          disabled={!(isCinFormChanged && isValid)}
          w={100}
          backgroundColor={
            isCinFormChanged && isValid
              ? ('PRIMARY' as ColorOptions)
              : (allThemes.light.colors.DISABLED_GREY as ColorOptions)
          }
          onPress={handlePress}>
          {cinSubmitHandler?.state === 'pending' ? (
            <Loader type="small" color="WHITE" />
          ) : (
            <Text
              color={
                isCinFormChanged && isValid
                  ? (allThemes.light.colors.WHITE as ColorOptions)
                  : (allThemes.light.colors.GRAY_LIGHT as ColorOptions)
              }
              type="body2">
              {'Update CIN'}
            </Text>
          )}
        </Box>
      </ModBox>
    )
  }

  const onDirectorSubmit = () => {
    if (identificationNumber?.length > 0) {
      const directorData = getDirectorChanges(identificationNumber)
      if (directorData?.length > 0) {
        directorSubmit(identificationNumber, 'DIRECTOR', directorData)
      }
    }
  }
  const onUboSubmit = () => {
    if (identificationNumber?.length > 0) {
      const uboData = getUboChanges(identificationNumber)
      if (uboData?.length > 0) {
        ubosSubmit(identificationNumber, 'UBO', uboData)
      }
    }
  }
  const onAuthSubmit = () => {
    if (identificationNumber?.length > 0) {
      const authData = getAuthChanges(identificationNumber)
      if (authData?.length > 0) {
        authsSubmit(identificationNumber, 'AUTHORISED_SIGNATORY', authData)
      }
    }
  }
  const exportFiuFile = (fileType: any) => lazyExportFiuFile('AUDIT', fileType, identificationNumber)

  return (
    <>
      <Box {...styles.container} marginBottom={32}>
        <Box {...styles.header}>
          <Text
            title={'Director / UBO / Authorised signatories Profile Information '}
            color={'BLACK_21'}
            fontSize={18}
            fontFamily={'jiotype-bold'}
          />
        </Box>
        <Box justifyContent="center" flexDirection="row">
          <Form formData={MerchantSearch()} actionButtons={handleSubmit} container={FormBox} />
        </Box>
        <ModBox show={showDetails} marginHorizontal={32}>
          <Text title={'CIN Details'} color={'BLACK_21'} fontSize={14} fontFamily={'jiotype-bold'} />
          <Box flexDirection="row" marginHorizontal={-16} w={'50%'}>
            <Form
              formData={CinDetailsForm(
                <Icon id={'CALENDAR'} size={15} color="PRIMARY" />,
                true,
                cinData && cinData?.date && cinData?.date?.includes('T')
                  ? cinData?.date?.split('T')?.[0]
                  : cinData?.date ?? '',
                exportFiuFile,
              )}
              actionButtons={handleCinUpdateAction}
              container={FormBox}
              initialValues={{
                cinNumber: cinData?.number,
                cinDate: cinData?.date?.includes('T') ? cinData?.date?.split('T')?.[0] : cinData?.date ?? '',
              }}
            />
          </Box>
          <ModBox show={showDetails}>
            <TableWithEditAddForm
              type={'director'}
              addChanges={addDirector}
              updateChanges={updateDirectorChanges}
              deleteRow={deleteDirector}
              listData={directorsListData(identificationNumber)}
              onSubmitPress={onDirectorSubmit}
              apiHandler={directorSubmitHandler}
              editedData={editedDirList}
              identificationNumber={identificationNumber}
              undoChanges={undoDirector}
            />
            <TableWithEditAddForm
              type={'ubo'}
              addChanges={addUbo}
              updateChanges={updateUboChanges}
              deleteRow={deleteUbo}
              listData={uboListData(identificationNumber)}
              onSubmitPress={onUboSubmit}
              apiHandler={ubosSubmitHandler}
              editedData={editedUboList}
              identificationNumber={identificationNumber}
              undoChanges={undoUbo}
            />
            <TableWithEditAddForm
              type={'auth'}
              addChanges={addAuthorisedSignatory}
              updateChanges={updateAuthSigChanges}
              deleteRow={deleteAuthSignatory}
              listData={authorizedListData(identificationNumber)}
              onSubmitPress={onAuthSubmit}
              apiHandler={authsSubmitHandler}
              editedData={editedAuthList}
              identificationNumber={identificationNumber}
              undoChanges={undoAuth}
            />
          </ModBox>
        </ModBox>
      </Box>
      <ModBox
        position={'absolute'}
        top={0}
        left={0}
        right={0}
        bottom={0}
        backgroundColor={'TRANSPARENT'}
        show={exportFiuFileHandler.state === 'pending'}
        justifyContent={'center'}>
        <LoaderNew color={'PRIMARY'} type={'large'} />
      </ModBox>
    </>
  )
}

export default FiuCompliance
